import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { derivarParametrosDoAgente } from '@/features/negociacaoIndividualWizard';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { alterarDadoDaSimulacao, buscarConfiguracaoDoAdmin, carregarContextoDoAdmin, escolherModoDaSimulacaoDoAdmin, iniciarSimulacaoDoAdmin, reiniciarSimulacaoDoAdmin } from '../store/actions';
import { selectConfiguracaoAlteradaNaExecucao, selectConfiguracaoDoAdmin, selectContextoDoAdmin, selectErroDoEncerramento, selectFormularioDaSimulacao, selectInicioDaSimulacao, selectSimulacaoAtual } from '../store/selectors';
import { valoresPadraoAplicados } from '../store/slice';
import type { ConfiguracaoDoAdminNoEstado, ModoDaSimulacao, ModoDisponivel, SituacaoDosCamposDeInicio } from '../types';
import { camposVisiveisDoInicio, errosDosCamposDoInicio, obrigatoriosPreenchidos, valoresPadraoDoAgente } from '../utils/camposDeInicio';
import { ehEstadoDoAdmin, identificacaoDaExecucao, modoDaConfiguracao, opcoesDeModo } from '../utils/execucaoDoAdmin';
import { textoDaIndisponibilidadeDoModo } from '../utils/rotulosDaSimulacao';
const TEXTO_SEM_MODO = 'Escolha o modo do teste para ver os dados necessários.';

/**
 * Sem dados e sem erro é a leitura que ainda não saiu: antes do primeiro dispatch e depois da
 * troca de escritório, que zera a configuração. Só a falha registrada vira "Não foi possível".
 */
const situacaoDosCampos = (modo: ModoDisponivel | null, configuracao: Pick<ConfiguracaoDoAdminNoEstado, 'dados' | 'erro'>, quantidadeDeCampos: number): SituacaoDosCamposDeInicio => {
  if (!configuracao.dados) return configuracao.erro ? 'erro' : 'carregando';
  if (!modo?.disponivel) return 'sem-agente';
  return quantidadeDeCampos === 0 ? 'sem-campos' : 'campos';
};
const textoSemCampos = (modo: ModoDisponivel | null): string => modo && !modo.disponivel ? textoDaIndisponibilidadeDoModo(modo.motivo) : TEXTO_SEM_MODO;

/**
 * Escritório, modo e dados de início da tela do admin (CA-11, CA-15, CA-17). Os campos vêm do
 * agente do modo escolhido, que a configuração do admin já traz: o catálogo do cliente não entra
 * (CA-20).
 */
export function useConfiguracaoDaSimulacaoDoAdmin() {
  const $ = _c(47);
  const dispatch = useAppDispatch();
  const contexto = useAppSelector(selectContextoDoAdmin);
  const configuracao = useAppSelector(selectConfiguracaoDoAdmin);
  const formulario = useAppSelector(selectFormularioDaSimulacao);
  const simulacao = useAppSelector(selectSimulacaoAtual);
  const erroDoEncerramento = useAppSelector(selectErroDoEncerramento);
  const configuracaoAlterada = useAppSelector(selectConfiguracaoAlteradaNaExecucao);
  const {
    iniciando,
    erro: erroDoInicio
  } = useAppSelector(selectInicioDaSimulacao);
  const escritorioId = contexto?.escritorioId ?? null;
  const flowId = contexto?.flowId ?? null;
  const modoEscolhido = contexto?.modo ?? null;
  const dados = configuracao.dados;
  const modo = dados && modoEscolhido ? modoDaConfiguracao(dados, modoEscolhido) : null;
  const agenteDoModo = modo?.disponivel ? modo.agente : null;
  const variables = camposVisiveisDoInicio(derivarParametrosDoAgente(agenteDoModo));
  const formularioId = formulario.flowId;
  let t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== escritorioId || $[2] !== flowId) {
    t0 = () => {
      if (escritorioId && flowId) {
        dispatch(carregarContextoDoAdmin());
      }
    };
    t1 = [dispatch, escritorioId, flowId];
    $[0] = dispatch;
    $[1] = escritorioId;
    $[2] = flowId;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  useEffect(() => {
    if (!formularioId || !agenteDoModo) {
      return;
    }
    const valores = valoresPadraoDoAgente(derivarParametrosDoAgente(agenteDoModo));
    dispatch(valoresPadraoAplicados({
      flowId: formularioId,
      valores
    }));
  }, [dispatch, formularioId, agenteDoModo]);
  let t2;
  if ($[5] !== dispatch) {
    t2 = novo => {
      dispatch(escolherModoDaSimulacaoDoAdmin(novo));
    };
    $[5] = dispatch;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const escolherModo = t2;
  let t3;
  if ($[7] !== dispatch) {
    t3 = (nome, valor) => {
      dispatch(alterarDadoDaSimulacao(nome, valor));
    };
    $[7] = dispatch;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const alterarDado = t3;
  let t4;
  if ($[9] !== dispatch || $[10] !== simulacao) {
    t4 = () => {
      if (simulacao) {
        dispatch(reiniciarSimulacaoDoAdmin());
      } else {
        dispatch(iniciarSimulacaoDoAdmin({
          motivo: "INICIO"
        }));
      }
    };
    $[9] = dispatch;
    $[10] = simulacao;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const iniciar = t4;
  let t5;
  if ($[12] !== dispatch || $[13] !== escritorioId || $[14] !== flowId) {
    t5 = () => {
      if (escritorioId && flowId) {
        dispatch(buscarConfiguracaoDoAdmin({
          flowId,
          escritorioId
        }));
      }
    };
    $[12] = dispatch;
    $[13] = escritorioId;
    $[14] = flowId;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const recarregarConfiguracao = t5;
  let t6;
  if ($[16] !== dados) {
    t6 = opcoesDeModo(dados);
    $[16] = dados;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = configuracao.carregando;
  const t8 = configuracao.erro;
  const t9 = dados?.agenteId === null;
  const t10 = simulacao?.agenteNome || modo?.agente?.name || "Lexi";
  let t11;
  if ($[18] !== dados || $[19] !== simulacao) {
    t11 = ehEstadoDoAdmin(simulacao) ? `${simulacao.agenteNome} · ${identificacaoDaExecucao(simulacao, dados)}` : null;
    $[18] = dados;
    $[19] = simulacao;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = situacaoDosCampos(modo, configuracao, variables.length);
  const t13 = textoSemCampos(modo);
  const t14 = formulario.valores;
  let t15;
  if ($[21] !== erroDoInicio) {
    t15 = errosDosCamposDoInicio(erroDoInicio);
    $[21] = erroDoInicio;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const t16 = simulacao !== null;
  const t17 = modo?.disponivel === true && !iniciando && !configuracao.carregando && obrigatoriosPreenchidos(variables, formulario.valores);
  let t18;
  if ($[23] !== alterarDado || $[24] !== configuracao.carregando || $[25] !== configuracao.erro || $[26] !== configuracaoAlterada || $[27] !== erroDoEncerramento || $[28] !== erroDoInicio || $[29] !== escolherModo || $[30] !== escritorioId || $[31] !== formulario.valores || $[32] !== iniciando || $[33] !== iniciar || $[34] !== modoEscolhido || $[35] !== recarregarConfiguracao || $[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t13 || $[40] !== t15 || $[41] !== t16 || $[42] !== t17 || $[43] !== t6 || $[44] !== t9 || $[45] !== variables) {
    t18 = {
      escritorioId,
      opcoesDeModo: t6,
      modo: modoEscolhido,
      carregandoConfiguracao: t7,
      erroDaConfiguracao: t8,
      fluxoSemAgente: t9,
      agentName: t10,
      identificacao: t11,
      configuracaoAlterada,
      situacaoDosCampos: t12,
      textoSemCampos: t13,
      variables,
      valores: t14,
      errors: t15,
      iniciando,
      erroDoInicio,
      erroDoEncerramento,
      started: t16,
      podeIniciar: t17,
      escolherModo,
      alterarDado,
      iniciar,
      recarregarConfiguracao
    };
    $[23] = alterarDado;
    $[24] = configuracao.carregando;
    $[25] = configuracao.erro;
    $[26] = configuracaoAlterada;
    $[27] = erroDoEncerramento;
    $[28] = erroDoInicio;
    $[29] = escolherModo;
    $[30] = escritorioId;
    $[31] = formulario.valores;
    $[32] = iniciando;
    $[33] = iniciar;
    $[34] = modoEscolhido;
    $[35] = recarregarConfiguracao;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t6;
    $[44] = t9;
    $[45] = variables;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  return t18;
}